import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  XCircle, FileText, Download, Eye, Clock, DollarSign,
  User, Calendar, AlertCircle, CheckCircle, XCircle as XCircleIcon,
  Star, MapPin, Timer, Paperclip, ChevronRight, ExternalLink
} from "lucide-react";
import { Escrow, EscrowStatus, escrowAPI } from "../../services/escrowAPI";
import { Badge } from "./badge";
import { ContractStatusEnum, ContractTypeEnum, MilestoneStatusEnum } from "../../modules/shared/model";
import OrderDetailsModal from "./OrderDetailsModal";
import {
  getContractStatusText,
  getContractStatusColor,
  getMilestoneStatusText,
  getMilestoneStatusColor,
  getPaymentStatusText,
  getPaymentStatusColor,
  defaultHandleRequestPaymentRelease
} from "../../utils/contractUtils";
import { GlobalService } from "../../modules/shared/services/global.service";
import { getHiringTax } from "../../utils/categoryFormSettingsUtils";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { fetchContract } from "../../redux/Slices/contractSlice";
import { calculateUserAverageRating } from "../../utils/ratingCalculationUtils";

interface ContractDetailModalProps {
  isOpen: boolean;
  onClose: (messageData?: any) => void;
  escrow: Escrow | null;
  loading?: boolean;
  onRefresh?: () => void; // Optional callback to refresh data after actions
  // Contract action props
  canAcceptDeclineContract?: (escrow: Escrow) => boolean;
  requiresTrackerCheckbox?: (escrow: Escrow) => boolean;
  trackerCheckboxStates?: { [contractId: string]: boolean };
  handleTrackerCheckboxChange?: (contractId: string, checked: boolean) => void;
  handleContractAction?: (contractId: string, action: "accept" | "decline") => void;
  setShowServicesDialog?: (show: boolean) => void;
  hideContactClientButton?: boolean; // Optional prop to hide Contact Client button
}

const ContractDetailModal: React.FC<ContractDetailModalProps> = ({
  isOpen,
  onClose,
  escrow,
  loading = false,
  onRefresh,
  canAcceptDeclineContract,
  requiresTrackerCheckbox,
  trackerCheckboxStates = {},
  handleTrackerCheckboxChange,
  handleContractAction,
  setShowServicesDialog,
  hideContactClientButton = false
}) => {
  const [localTrackerCheckboxStates, setLocalTrackerCheckboxStates] = useState<{ [contractId: string]: boolean }>({});
  const [fetchedEscrow, setFetchedEscrow] = useState<Escrow | null>(null);
  const [isLoadingEscrow, setIsLoadingEscrow] = useState(false);
  const [contractData, setContractData] = useState<any>(null);
  const [milestonesData, setMilestonesData] = useState<any[]>([]);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state: any) => state.auth);
  const { setting } = useAppSelector((state: any) => state.categoryFormSettings);

  // Fetch contract data when modal opens
  useEffect(() => {
    const fetchContractData = async () => {
      if (!isOpen || !escrow?.contract?.id) return;

      setIsInitialLoading(true);
      setIsLoadingContract(true);
      try {
        // Fetch contract data
        const contractRes = await dispatch(fetchContract(escrow.contract.id));
        if (contractRes?.meta?.requestStatus === "fulfilled") {
          const contract = contractRes.payload as any;
          setContractData(contract);
          
          // Process milestones similar to myorders.tsx
          const milestones = contract?.milestones || [];
          if (milestones.length > 0) {
            const escrows = contract?.escrows || [];
            
            // Map escrows to milestones
            const milestonesWithEscrows = milestones.map((milestone: any) => {
              const milestoneEscrow = escrows.find((e: any) => e.milestone?.id === milestone.id);
              let paymentStatus = "no_payment"; // Default status

              if (milestoneEscrow) {
                const statusName = milestoneEscrow.status?.name?.toLowerCase() || milestoneEscrow.status?.toString().toLowerCase() || "";
                switch (statusName) {
                  case "paid":
                  case "funded":
                  case "completed":
                    paymentStatus = "paid";
                    break;
                  case "pending":
                  case "created":
                    paymentStatus = "pending";
                    break;
                  case "failed":
                  case "cancelled":
                    paymentStatus = "failed";
                    break;
                  case "released":
                    paymentStatus = "released";
                    break;
                  case "refunded":
                    paymentStatus = "refunded";
                    break;
                  default:
                    paymentStatus = "pending";
                }
              }
              return {
                ...milestone,
                escrow: milestoneEscrow,
                paymentStatus
              };
            });
            setMilestonesData(milestonesWithEscrows);
          } else {
            setMilestonesData([]);
          }
        }
      } catch (error) {
        console.error("Error fetching contract data:", error);
        GlobalService.showToast("error", "Error", "Failed to fetch contract details");
      } finally {
        setIsLoadingContract(false);
      }
    };

    fetchContractData();
  }, [isOpen, escrow?.contract?.id, dispatch]);

  if (!isOpen || !escrow) return null;

  // Use local state if no external state is provided
  const currentTrackerStates = trackerCheckboxStates || localTrackerCheckboxStates;
  const handleTrackerChange = handleTrackerCheckboxChange || ((contractId: string, checked: boolean) => {
    setLocalTrackerCheckboxStates(prev => ({ ...prev, [contractId]: checked }));
  });

  // Map contract data to order format (similar to myorders.tsx)
  const mapContractToOrder = (contract: any) => {
    if (!contract) return null;
    
    const contractType = Number(contract?.type?.id) === 2 ? "hourly" : "fixed";
    const isHourly = contractType === "hourly";

    // Calculate platform fees and totals
    const calculatePlatformFee = (amount: number) => amount * 0.02; // 2% platform fee
    const calculateTotalWithFee = (amount: number) => amount;

    // Check if contract is emergency and get emergency cost from first milestone
    const isEmergency = contract?.offer?.job?.isEmergency || contract?.isEmergency || false;
    const firstMilestone = milestonesData[0];
    const emergencyCost = isEmergency && firstMilestone ? Number(firstMilestone.emergencyCost || 0) : 0;
    const trackerCost = firstMilestone ? Number(firstMilestone.trackerAmount || 0) : 0;

    // For hourly contracts, calculate weekly amount
    const weeklyAmount = isHourly ? (Number(contract.amount || 0) * Number(contract.weeklyLimit || 0)) : Number(contract.amount || 0);
    const platformFee = calculatePlatformFee(weeklyAmount + emergencyCost + trackerCost);
    const totalAmount = calculateTotalWithFee(weeklyAmount + emergencyCost);

    // Calculate average rating for the client from milestone reviews
    const clientId = contract?.client?.id;
    let clientRatingData = { average: 0, count: 0 };

    if (clientId) {
      // Extract all reviews from all milestones in this contract
      const allContractReviews = milestonesData.flatMap(milestone => milestone.reviews || []);
      clientRatingData = calculateUserAverageRating(allContractReviews, user?.id || "");
    }

    return {
      id: contract.id,
      title: contract.title || "Contract",
      client: contract?.client?.firstName ? `${contract.client.firstName} ${contract.client.lastName || ""}`.trim() : (contract?.client?.id || "Client"),
      clientInfo: {
        ...contract?.client,
        rating: contract.client?.averageRating,
        reviews: contract.client?.reviewsCount
      },
      amount: Number(contract.amount || 0),
      weeklyAmount: weeklyAmount,
      platformFee: platformFee,
      totalAmount: totalAmount,
      contractType: contractType,
      isHourly: isHourly,
      weeklyLimit: contract.weeklyLimit || 0,
      enableTracker: contract.enableTracker || false,
      status: Number(contract?.status?.id),
      date: contract.createdAt || new Date().toISOString(),
      dueDate: contract.dueDate || contract.updatedAt || new Date().toISOString(),
      description: contract.description || "",
      location: contract.location || "",
      messages: 0,
      attachments: contract?.files?.length || 0,
      files: contract?.files || [],
      clientRating: clientRatingData.average,
      isPriority: false,
      paymentStatus: "paid",
      timeline: [],
      milestones: milestonesData,
      // Emergency and tracker information
      isEmergency: isEmergency,
      emergencyCost: emergencyCost,
      trackerCost: trackerCost
    };
  };

  // Create order from contract data
  const selectedOrder = contractData ? mapContractToOrder(contractData) : null;

  // Use shared utility for payment release request
  const handleRequestPaymentRelease = async (milestoneId: string) => {
    await defaultHandleRequestPaymentRelease(milestoneId);
  };

  // Helper functions for status colors and text
  const getEscrowStatusColor = (statusId?: EscrowStatus) => {
    switch (statusId) {
      case EscrowStatus.PENDING:
        return "bg-yellow-100 text-yellow-800";
      case EscrowStatus.FUNDED:
        return "bg-blue-100 text-blue-800";
      case EscrowStatus.RELEASED:
        return "bg-green-100 text-green-800";
      case EscrowStatus.DISPUTED:
        return "bg-red-100 text-red-800";
      case EscrowStatus.RESOLVED:
        return "bg-green-100 text-green-800";
      case EscrowStatus.REFUNDED:
        return "bg-gray-100 text-gray-800";
      case EscrowStatus.CANCELLED:
        return "bg-red-100 text-red-800";
      case EscrowStatus.FAILED:
        return "bg-red-100 text-red-800";
      case EscrowStatus.WITHDRAWN:
        return "bg-green-100 text-green-800";
      case EscrowStatus.REQUEST_WITHDRAW:
        return "bg-green-100 text-green-800";
      case EscrowStatus.USER_FUNDED:
        return "bg-green-100 text-green-800";
      case EscrowStatus.CREDITS_PURCHASED:
        return "bg-purple-100 text-purple-800";
      case EscrowStatus.QUOTE_PURCHASED:
        return "bg-indigo-100 text-indigo-800";
      case EscrowStatus.USED_CREDIT_PURCHASED:
        return "bg-purple-100 text-purple-800";
      case EscrowStatus.USED_QUOTE_PURCHASED:
        return "bg-indigo-100 text-indigo-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getEscrowStatusText = (statusId?: EscrowStatus) => {
    switch (statusId) {
      case EscrowStatus.PENDING:
        return "Pending";
      case EscrowStatus.FUNDED:
        return "Funded";
      case EscrowStatus.RELEASED:
        return "Released";
      case EscrowStatus.DISPUTED:
        return "Disputed";
      case EscrowStatus.RESOLVED:
        return "Resolved";
      case EscrowStatus.REFUNDED:
        return "Refunded";
      case EscrowStatus.CANCELLED:
        return "Cancelled";
      case EscrowStatus.FAILED:
        return "Failed";
      case EscrowStatus.WITHDRAWN:
        return "Withdrawn";
      case EscrowStatus.REQUEST_WITHDRAW:
        return "Withdraw Request";
      case EscrowStatus.USER_FUNDED:
        return "User Funded";
      case EscrowStatus.CREDITS_PURCHASED:
        return "Credits Purchased";
      case EscrowStatus.QUOTE_PURCHASED:
        return "Quote Purchased";
      case EscrowStatus.USED_CREDIT_PURCHASED:
        return "Used Credit Purchased";
      case EscrowStatus.USED_QUOTE_PURCHASED:
        return "Used Quote Purchased";
      default:
        return "Unknown";
    }
  };

  const formatCurrency = (amount?: number | null, currency?: any) => {
    if (!amount) return "$0.00";
    const currencyCode = currency?.code || currency?.name || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(amount);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <AnimatePresence>
        {isLoadingContract ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg p-8 flex flex-col items-center justify-center"
          >
            <Clock className="h-8 w-8 animate-spin text-blue-600 mb-4" />
            <p className="text-gray-600">Loading contract details...</p>
          </motion.div>
        ) : selectedOrder ? (
          <OrderDetailsModal
            order={selectedOrder}
            onClose={onClose}
            onRefresh={onRefresh}
            hideContactClientButton={hideContactClientButton}
          />
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
};

export default ContractDetailModal;
